/**
 * Tenant Activity Log — timeline panel showing tenant lifecycle events.
 * @see Documentation/Specification/04_Processes/application-agnostic/tenant/TenantLifecycle.md
 */
import { useGetList } from 'react-admin';
import {
  Box, Typography, Paper, Chip, CircularProgress,
} from '@mui/material';
import { History, Block, CheckCircle, DeleteForever, Edit } from '@mui/icons-material';

interface ActivityEntry {
  id: string;
  action: string;
  actor: string;
  reason?: string;
  previousStatus?: string;
  newStatus?: string;
  timestamp: string;
}

const ACTION_ICONS: Record<string, React.ReactNode> = {
  'Active→Suspended': <Block sx={{ fontSize: 18, color: 'warning.main' }} />,
  'Suspended→Active': <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />,
  'Active→Liquidated': <DeleteForever sx={{ fontSize: 18, color: 'error.main' }} />,
  'Suspended→Liquidated': <DeleteForever sx={{ fontSize: 18, color: 'error.main' }} />,
};

const ACTION_COLORS: Record<string, 'warning' | 'success' | 'error' | 'default'> = {
  'Active→Suspended': 'warning',
  'Suspended→Active': 'success',
  'Active→Liquidated': 'error',
  'Suspended→Liquidated': 'error',
};

interface Props {
  tenantId: string;
}

export const TenantActivityLog = ({ tenantId }: Props) => {
  const { data: result, isLoading } = useGetList('tenants', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'timestamp', order: 'DESC' },
    filter: { tenantId },
  });

  // The activity log comes from a custom endpoint, not the list provider.
  // For now, show a placeholder that will be wired to the activity endpoint.
  if (isLoading) {
    return <Box sx={{ p: 2 }}><CircularProgress size={24} /></Box>;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight={600} mb={2}>
        <History sx={{ mr: 1, verticalAlign: 'middle' }} />
        Activity Log
      </Typography>
      <Paper sx={{ p: 2 }}>
        <Typography color="text.secondary" variant="body2">
          Activity logging will display lifecycle transitions (suspend, reactivate, liquidate) and configuration changes.
          Connected to the tenant activity API endpoint.
        </Typography>
      </Paper>
    </Box>
  );
};
